import * as React from 'react';
import { observer } from 'mobx-react';

import { wazzupLinks } from 'shared/entities/app';
import {
  mapWzOAuthErrorCodeToMessage,
  WzOAuthClientError
} from 'shared/entities/channels';
import { ComponentType } from 'shared/entities/components/Component';
import { useTypedTranslation } from 'shared/entities/localization';
import { ButtonSize, ButtonStyle } from 'shared/newEntities/components/Button';
import { Button } from 'shared/newComponents/buttons';
import ExternalLink from 'shared/newComponents/ExternalLink';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import Typography from 'shared/newComponents/Typography';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import { useRootStore } from 'stores/index';
import { useChannelsModalStore } from 'stores/ChannelsModalStore';

import './WzOAuthAction.modules.scss';

const DESCRIPTION_KEYS = {
  connect: 'wzOAuth.description',
  migrate: 'wzOAuth.descriptionMigrate',
  reconnect: 'wzOAuth.descriptionReconnect'
} as const;

type Props = {
  /** Канал, который переподключается или переводится с ключа; без него подключается новый канал */
  channelId?: string;
  action: 'connect' | 'reconnect' | 'migrate';
  /** Доступ отозван: для переподключения показываем текст про потерю доступа, а не про рабочий канал */
  isRevoked?: boolean;
};

const WzOAuthAction: React.FC<Props> = ({ channelId, action, isRevoked }) => {
  const rootStore = useRootStore();
  const { store: modalStore } = useChannelsModalStore();
  const { t } = useTypedTranslation('newContainers');
  const oauth = modalStore.wzOAuth;
  const targetChannelId = channelId ?? null;
  // состояние попытки общее на модалку: чужие ошибки в этом месте не показываем
  const isTarget = oauth.targetChannelId === targetChannelId;
  const canEdit = rootStore.permissionsStore.canEditProject;

  const connect = React.useCallback(() => {
    void oauth.connectChannel(targetChannelId);
  }, [oauth, targetChannelId]);

  const waiting = oauth.authorizationStage.isLoading;

  return (
    <div styleName="wz-oauth">
      <Typography
        fontType={TypographyType.text}
        fontSize={TypographySize.s}
        fontColor={TypographyColor.main}
      >
        {t(
          action === 'reconnect' && isRevoked
            ? 'wzOAuth.descriptionRevoked'
            : DESCRIPTION_KEYS[action]
        )}
      </Typography>
      {isTarget && oauth.errorCode && (
        <Typography
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={TypographyColor.error}
        >
          {mapWzOAuthErrorCodeToMessage(oauth.errorCode)(
            rootStore.translationsStore.t
          )}
        </Typography>
      )}
      {isTarget && oauth.statusUnavailable && (
        <Typography
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={TypographyColor.warning}
        >
          {t('wzOAuth.statusUnavailable')}
        </Typography>
      )}
      {isTarget && oauth.fallbackUrl && (
        <div>
          <Typography
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
          >
            {mapWzOAuthErrorCodeToMessage(WzOAuthClientError.popupBlocked)(
              rootStore.translationsStore.t
            )}
          </Typography>
          <ExternalLink
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.link}
            underline
            href={oauth.fallbackUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('wzOAuth.openAuthorization')}
          </ExternalLink>
        </div>
      )}
      {waiting ? (
        <div styleName="wz-oauth__actions">
          <div styleName="wz-oauth__waiting">
            <LoaderSpinner
              size={LoaderSpinnerSize.sm}
              color={LoaderSpinnerColor.blue}
            />
            <Typography
              fontType={TypographyType.text}
              fontSize={TypographySize.s}
            >
              {t(isTarget ? 'wzOAuth.waiting' : 'wzOAuth.waitingOther')}
            </Typography>
          </div>
          <Button
            type={ComponentType.button}
            size={ButtonSize.m}
            style={ButtonStyle.transparent}
            onClick={oauth.stopWaiting}
          >
            {t('wzOAuth.cancel')}
          </Button>
        </div>
      ) : (
        <Button
          type={ComponentType.button}
          size={ButtonSize.m}
          onClick={connect}
          disabled={!canEdit || oauth.authorizationStage.isLoading}
        >
          {t(`wzOAuth.${action}`)}
        </Button>
      )}
      {action === 'connect' && (
        <div styleName="wz-oauth__register">
          <Typography
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.main}
          >
            {t('wzOAuth.registerPrompt')}
          </Typography>
          <ExternalLink
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.link}
            underline
            href={wazzupLinks.referal}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('wzOAuth.registerLink')}
          </ExternalLink>
        </div>
      )}
    </div>
  );
};

export default observer(WzOAuthAction);
